import React, { useState, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Typography,
  Box,
} from '@mui/material';
import { SearchInput } from '../SearchInput';
import { getGroupedCPTCodesBySubrole, normalizeCPTCode } from '../../constants/CPTCodes';

const CPTCodesDropdownSingle = ({
  value,
  options,
  onChange,
  label = 'CPT Code',
  disabled = false,
  sx = {},
  onOpen = () => { },
  subrole,
  useGroupedDisplay = false,
  renderValue,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const groupedCodes = useMemo(() => {
    if (useGroupedDisplay && subrole) {
      return getGroupedCPTCodesBySubrole(subrole);
    }
    return null;
  }, [subrole, useGroupedDisplay]);

  const groupedOptions = useMemo(() => {
    if (!useGroupedDisplay || !groupedCodes) {
      return null;
    }

    if (!options || !Array.isArray(options) || options.length === 0) {
      return {
        subroleLabel: groupedCodes.subroleLabel,
        subroleCodes: groupedCodes.subroleCodes || [],
        otherCodes: groupedCodes.otherCodes || [],
      };
    }

    const subroleCodeNumbers = new Set(
      groupedCodes.subroleCodes.map((code) => normalizeCPTCode(code.code))
    );

    const subroleOptions = options.filter((opt) =>
      subroleCodeNumbers.has(normalizeCPTCode(opt.code))
    );

    const otherOptions = options.filter(
      (opt) => !subroleCodeNumbers.has(normalizeCPTCode(opt.code))
    );

    return {
      subroleLabel: groupedCodes.subroleLabel,
      subroleCodes: subroleOptions,
      otherCodes: otherOptions,
    };
  }, [useGroupedDisplay, groupedCodes, options]);

  const filteredOptions = useMemo(() => {
    const codesToFilter =
      groupedCodes && useGroupedDisplay
        ? [
          ...(groupedOptions?.subroleCodes || []),
          ...(groupedOptions?.otherCodes || []),
        ]
        : options;

    if (!searchTerm) return codesToFilter;

    return codesToFilter.filter((option) => {
      const searchText = `${option.code} ${option.label}`.toLowerCase();
      return searchText.includes(searchTerm.toLowerCase());
    });
  }, [options, searchTerm, groupedCodes, groupedOptions, useGroupedDisplay]);

  const filteredGroupedCodes = useMemo(() => {
    if (!groupedOptions || !searchTerm) return groupedOptions;

    const filterCodes = (codes) => {
      return codes.filter((option) => {
        const searchText = `${option.code} ${option.label}`.toLowerCase();
        return searchText.includes(searchTerm.toLowerCase());
      });
    };

    return {
      subroleLabel: groupedOptions.subroleLabel,
      subroleCodes: filterCodes(groupedOptions.subroleCodes),
      otherCodes: filterCodes(groupedOptions.otherCodes),
    };
  }, [groupedOptions, searchTerm]);

  const normalizedValue = useMemo(() => {
    if (!value && value !== 0) return '';
    return String(value);
  }, [value]);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    const normalizedSelectedValue = selectedValue === '' ? '' : String(selectedValue);
    if (onChange && typeof onChange === 'function') {
      onChange(normalizedSelectedValue);
    }
  };

  const handleMenuItemClick = (optionCode, event) => {
    const normalizedSelectedValue = String(optionCode);
    setMenuOpen(false);

    if (onChange && typeof onChange === 'function') {
      setTimeout(() => {
        onChange(normalizedSelectedValue);
      }, 0);
    }
  };

  const handleOpen = () => {
    setSearchTerm('');
    setMenuOpen(true);
    onOpen();
  };

  const handleClose = () => {
    setSearchTerm('');
    setMenuOpen(false);
  };

  const allOptions = useMemo(() => {
    let baseOptions = [];

    if (useGroupedDisplay && groupedOptions) {
      baseOptions = [...groupedOptions.subroleCodes, ...groupedOptions.otherCodes];

      if (groupedCodes) {
        const allGroupedCodes = [
          ...(groupedCodes.subroleCodes || []),
          ...(groupedCodes.otherCodes || [])
        ];

        const baseCodeSet = new Set(baseOptions.map(opt => normalizeCPTCode(opt.code)));
        allGroupedCodes.forEach(code => {
          const normalizedCode = normalizeCPTCode(code.code);
          if (!baseCodeSet.has(normalizedCode)) {
            baseOptions.push(code);
            baseCodeSet.add(normalizedCode);
          }
        });
      }
    } else {
      baseOptions = options || [];
    }

    if (normalizedValue) {
      const foundInBase = baseOptions.find(
        (opt) => normalizeCPTCode(opt.code) === normalizedValue
      );

      if (!foundInBase && options && Array.isArray(options) && options.length > 0) {
        const foundInOriginal = options.find(
          (opt) => normalizeCPTCode(opt.code) === normalizedValue
        );
        if (foundInOriginal) {
          return [...baseOptions, foundInOriginal];
        }
      }
    }

    return baseOptions;
  }, [useGroupedDisplay, groupedOptions, options, normalizedValue, groupedCodes]);

  const getSelectedOption = () => {
    if (!normalizedValue) return null;

    if (allOptions && allOptions.length > 0) {
      const found = allOptions.find((opt) => normalizeCPTCode(opt.code) === normalizedValue);
      if (found) return found;
    }

    if (options && Array.isArray(options) && options.length > 0) {
      const found = options.find((opt) => normalizeCPTCode(opt.code) === normalizedValue);
      if (found) return found;
    }

    if (useGroupedDisplay && groupedCodes) {
      const allGroupedCodes = [
        ...(groupedCodes.subroleCodes || []),
        ...(groupedCodes.otherCodes || [])
      ];
      const found = allGroupedCodes.find((opt) => normalizeCPTCode(opt.code) === normalizedValue);
      if (found) return found;
    }

    return null;
  };

  const selectedOption = getSelectedOption();
  const isEmpty = !normalizedValue;

  return (
    <FormControl fullWidth sx={{ ...sx }}>
      <InputLabel
        id='cpt-codes-dropdown-single-label'
        shrink={!isEmpty ? true : undefined}
      >
        {label}
      </InputLabel>
      <Select
        labelId='cpt-codes-dropdown-single-label'
        id='cpt-codes-dropdown-single'
        value={normalizedValue}

        onChange={handleChange}
        disabled={disabled}
        multiple={false}
        displayEmpty
        notched={!isEmpty ? true : undefined}
        open={menuOpen}
        renderValue={(selected) => {
          if (renderValue) {
            return renderValue(selected, selectedOption, allOptions);
          }

          if (!selected || selected === '') {
            return '';
          }

          return (
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedOption
                ? `${selectedOption.code} - ${selectedOption.label}`
                : selected}
            </Typography>
          );
        }}
        MenuProps={{
          PaperProps: {
            style: { maxHeight: 300, zIndex: 1600 },
          },
          autoFocus: false,
          disablePortal: true,
          disableAutoFocusItem: true,
          disableScrollLock: true,
        }}
        onOpen={handleOpen}
        onClose={handleClose}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--clr-theme-purple)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--clr-theme-purple)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--clr-theme-purple)',
          },
        }}
      >
        <ListSubheader
          disableSticky
          onMouseDown={(e) => e.stopPropagation()}
        >
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            message='Search CPT codes...'
          />
        </ListSubheader>
        {useGroupedDisplay && groupedCodes && filteredGroupedCodes ? (
          <>
            {filteredGroupedCodes.subroleLabel &&
              filteredGroupedCodes.subroleCodes.length > 0 && (
                <>
                  <ListSubheader
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {filteredGroupedCodes.subroleLabel}
                  </ListSubheader>
                  {filteredGroupedCodes.subroleCodes.map((option) => (
                    <MenuItem
                      key={option.code}
                      value={String(option.code)}
                      onClick={(e) => handleMenuItemClick(option.code, e)}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {option.code}
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{ color: 'text.secondary' }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </>
              )}

            {filteredGroupedCodes.otherCodes.length > 0 && (
              <>
                {filteredGroupedCodes.subroleLabel &&
                  filteredGroupedCodes.subroleCodes.length > 0 && (
                    <ListSubheader
                      sx={{
                        fontWeight: 600,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        borderTop: '1px solid',
                      }}
                    >
                      Other
                    </ListSubheader>
                  )}
                {filteredGroupedCodes.otherCodes.map((option) => (
                  <MenuItem
                    key={option.code}
                    value={String(option.code)}
                    onClick={(e) => handleMenuItemClick(option.code, e)}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {option.code}
                      </Typography>
                      <Typography
                        variant='caption'
                        sx={{ color: 'text.secondary' }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </>
            )}
            {selectedOption &&
              !filteredGroupedCodes.subroleCodes.find(
                (opt) => normalizeCPTCode(opt.code) === normalizedValue
              ) &&
              !filteredGroupedCodes.otherCodes.find(
                (opt) => normalizeCPTCode(opt.code) === normalizedValue
              ) && (
                <MenuItem key={selectedOption.code} value={String(selectedOption.code)}>
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {selectedOption.code}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{ color: 'text.secondary' }}
                    >
                      {selectedOption.label}
                    </Typography>
                  </Box>
                </MenuItem>
              )}
            {filteredGroupedCodes.subroleCodes.length === 0 &&
              filteredGroupedCodes.otherCodes.length === 0 &&
              !selectedOption && (
                <MenuItem disabled>
                  <Typography
                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                  >
                    No CPT codes found
                  </Typography>
                </MenuItem>
              )}
          </>
        ) : filteredOptions.length > 0 ? (
          <>
            {filteredOptions.map((option) => (
              <MenuItem
                key={option.code}
                value={String(option.code)}
                onClick={(e) => handleMenuItemClick(option.code, e)}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {option.code}
                  </Typography>
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {option.label}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
            {selectedOption &&
              !filteredOptions.find(
                (opt) => normalizeCPTCode(opt.code) === normalizedValue
              ) && (
                <MenuItem key={selectedOption.code} value={String(selectedOption.code)}>
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {selectedOption.code}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                      {selectedOption.label}
                    </Typography>
                  </Box>
                </MenuItem>
              )}
          </>
        ) : (
          <>
            {selectedOption ? (
              <MenuItem key={selectedOption.code} value={String(selectedOption.code)}>
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {selectedOption.code}
                  </Typography>
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {selectedOption.label}
                  </Typography>
                </Box>
              </MenuItem>
            ) : (
              <MenuItem disabled>
                <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  No CPT codes found
                </Typography>
              </MenuItem>
            )}
          </>
        )}
      </Select>
    </FormControl>
  );
};

export default CPTCodesDropdownSingle;

